import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Loads data for the SuperAdmin panel based on the active tab.
 * Centralizes the per-tab fetch logic that lived inline in SuperAdmin.jsx.
 */
export default function useSuperAdminData(activeTab, orderFilters) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [logs, setLogs] = useState([]);
  const [credentials, setCredentials] = useState(null);
  const [apiConnections, setApiConnections] = useState([]);
  const [prodConfig, setProdConfig] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [transferConfig, setTransferConfig] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [serverInfo, setServerInfo] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        if (activeTab === 'dashboard') {
          const statsRes = await axios.get(`${BACKEND_URL}/api/super-admin/stats`, { headers });
          if (!cancelled) setStats(statsRes.data);
          try {
            const ipRes = await axios.get(`${BACKEND_URL}/api/super-admin/server-ip`, { headers });
            if (!cancelled) setServerInfo(ipRes.data);
          } catch {
            if (!cancelled) setServerInfo({ public_ip: 'Error al obtener', banco_plaza_environment: 'desconocido', banco_plaza_url: '', error: true });
          }
          try {
            const rateRes = await axios.get(`${BACKEND_URL}/api/super-admin/exchange-rate`, { headers });
            if (!cancelled) setExchangeRate(rateRes.data);
          } catch (e) { console.warn('Exchange rate not available:', e.message); }
        } else if (activeTab === 'merchants') {
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/merchants`, { headers });
          if (!cancelled) setMerchants(res.data);
        } else if (activeTab === 'logs') {
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/logs?limit=50`, { headers });
          if (!cancelled) setLogs(res.data);
        } else if (activeTab === 'credentials') {
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/credentials`, { headers });
          if (!cancelled) setCredentials(res.data);
        } else if (activeTab === 'wallets' || activeTab === 'commission-wallets') {
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/all-wallets`, { headers });
          if (!cancelled) setWallets(res.data || []);
        } else if (activeTab === 'orders') {
          const params = new URLSearchParams();
          if (orderFilters.status) params.append('status_filter', orderFilters.status);
          if (orderFilters.paymentMethod) params.append('payment_method', orderFilters.paymentMethod);
          if (orderFilters.project) params.append('project_id', orderFilters.project);
          if (orderFilters.dateFrom) params.append('date_from', orderFilters.dateFrom);
          if (orderFilters.dateTo) params.append('date_to', orderFilters.dateTo);
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/all-orders?${params.toString()}`, { headers });
          if (!cancelled) setOrders(res.data || []);
        } else if (activeTab === 'transfers') {
          const configRes = await axios.get(`${BACKEND_URL}/api/super-admin/transfer-config`, { headers });
          const transfersRes = await axios.get(`${BACKEND_URL}/api/super-admin/transfers/history?limit=50`, { headers });
          if (!cancelled) {
            setTransferConfig(configRes.data);
            setTransfers(transfersRes.data || []);
          }
        } else if (activeTab === 'api-connections') {
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/api-connections`, { headers });
          if (!cancelled) setApiConnections(res.data || []);
        } else if (activeTab === 'production') {
          const res = await axios.get(`${BACKEND_URL}/api/super-admin/production-config`, { headers });
          if (!cancelled) setProdConfig(res.data);
        }
      } catch (error) {
        console.error('Error loading super admin data:', error);
        if (error.response?.status === 403) {
          alert('No tienes permisos de Super Admin');
          navigate('/dashboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, reloadKey, orderFilters, navigate]);

  return {
    loading,
    stats, merchants, logs, credentials, apiConnections, prodConfig,
    wallets, orders, transferConfig, transfers, serverInfo, exchangeRate,
    setExchangeRate,
    reload,
  };
}
